import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from 'constants/queryKeys';
import {
  fetchSchools,
  fetchSchoolBySlug,
  fetchSchoolCatalog,
  fetchCourses,
  fetchCourseById,
  fetchLessonById,
  fetchRelatedLessons,
  fetchBatchById,
  fetchMyEnrolledBatches,
  fetchMyBatchEnrollment,
  fetchMyBatchLiveSchedules,
} from 'shared/data/repositories';
import { getBnnTopicCount } from 'schools/bnn/data/bnnSyllabus';
import { getJaiminiTopicCount } from 'schools/jaimini/data/jaiminiSyllabus';
import { getKpTopicCount } from 'schools/kp/data/kpSyllabus';
import { getLalKitabTopicCount } from 'schools/lalKitab/data/lalKitabSyllabus';
import { getNumerologyTopicCount } from 'schools/numerology/data/numerologySyllabus';
import { getSwaraTopicCount } from 'schools/swaraVigyan/data/swaraSyllabus';
import { getYantraTopicCount } from 'schools/yantraVidya/data/yantraVidyaSyllabus';
import { getTantraTopicCount } from 'schools/tantra/data/tantraSyllabus';
import type { School, Course, Lesson, Batch, Assignment } from 'types';
import type { CatalogCategory } from 'features/schools/utils/schoolCatalog';

export function useSchools(): UseQueryResult<School[]> {
  return useQuery<School[]>({
    queryKey: queryKeys.schools.all,
    queryFn: fetchSchools,
  });
}

export function useSchoolCatalog(): UseQueryResult<CatalogCategory[]> {
  return useQuery<CatalogCategory[]>({
    queryKey: queryKeys.schools.catalog,
    queryFn: fetchSchoolCatalog,
  });
}

export function useSchool(slug: string): UseQueryResult<School> {
  return useQuery<School>({
    queryKey: queryKeys.schools.detail(slug),
    queryFn: () => fetchSchoolBySlug(slug),
    enabled: !!slug,
  });
}

export function useCourses(schoolSlug?: string): UseQueryResult<Course[]> {
  return useQuery<Course[]>({
    queryKey: queryKeys.courses.list(schoolSlug),
    queryFn: () => fetchCourses(schoolSlug),
  });
}

const PARASHARI_LESSON_FALLBACK = 169;

export function useSchoolLessonCounts(): Record<string, number> {
  const { data: courses } = useCourses();

  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const course of courses ?? []) {
      counts[course.schoolSlug] = (counts[course.schoolSlug] ?? 0) + course.lessonCount;
    }
    if (!counts.parashari) {
      counts.parashari = PARASHARI_LESSON_FALLBACK;
    }
    if (!counts.jaimini) {
      counts.jaimini = getJaiminiTopicCount();
    }
    if (!counts.bnn) {
      counts.bnn = getBnnTopicCount();
    }
    if (!counts.kp) {
      counts.kp = getKpTopicCount();
    }
    if (!counts['lal-kitab']) {
      counts['lal-kitab'] = getLalKitabTopicCount();
    }
    if (!counts.numerology) {
      counts.numerology = getNumerologyTopicCount();
    }
    if (!counts['swara-vigyan']) {
      counts['swara-vigyan'] = getSwaraTopicCount();
    }
    if (!counts['yantra-vidya']) {
      counts['yantra-vidya'] = getYantraTopicCount();
    }
    if (!counts.tantra) {
      counts.tantra = getTantraTopicCount();
    }
    return counts;
  }, [courses]);
}

export function useCourse(id: string): UseQueryResult<Course> {
  return useQuery<Course>({
    queryKey: queryKeys.courses.detail(id),
    queryFn: () => fetchCourseById(id),
    enabled: !!id,
  });
}

export function useLesson(id: string): UseQueryResult<Lesson> {
  return useQuery<Lesson>({
    queryKey: queryKeys.lessons.detail(id),
    queryFn: () => fetchLessonById(id),
    enabled: !!id,
  });
}

export function useRelatedLessons(id: string): UseQueryResult<Lesson[]> {
  return useQuery<Lesson[]>({
    queryKey: queryKeys.lessons.related(id),
    queryFn: () => fetchRelatedLessons(id),
    enabled: !!id,
  });
}

export function useBatches(): UseQueryResult<Batch[]> {
  return useQuery<Batch[]>({
    queryKey: queryKeys.batches.all,
    queryFn: fetchMyEnrolledBatches,
  });
}

export function useBatch(id: string): UseQueryResult<Batch & { assignments?: Assignment[] }> {
  return useQuery<Batch & { assignments?: Assignment[] }>({
    queryKey: queryKeys.batches.detail(id),
    queryFn: () => fetchBatchById(id),
    enabled: !!id,
  });
}

export function useMyBatchEnrollment(batchId: string) {
  return useQuery({
    queryKey: queryKeys.enrollments.batch(batchId),
    queryFn: () => fetchMyBatchEnrollment(batchId),
    enabled: !!batchId,
  });
}

export function useBatchLiveSchedules(batchId: string) {
  return useQuery({
    queryKey: queryKeys.schedules.batch(batchId),
    queryFn: () => fetchMyBatchLiveSchedules(batchId),
    enabled: !!batchId,
  });
}
